import { APIURL } from "./config";

// ─── Types ────────────────────────────────────────────────────────────────────

export type PromptConfigPreset = {
  id: string;
  name: string;
  configJson: string;
};

export type CreatePresetPayload = {
  name: string;
  configJson: string;
};

export type UpdatePresetPayload = {
  name: string;
  configJson: string;
};

// ─── API Functions ────────────────────────────────────────────────────────────

/** Returns all presets ordered by name. */
export async function GetPromptConfigPresets(token: string): Promise<PromptConfigPreset[]> {
  const res = await fetch(`${APIURL}/PromptConfigPresets`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Creates a new preset.
 * Throws if name is blank or a preset with that name already exists.
 * Never upserts — create and update are strictly separate.
 */
export async function CreatePromptConfigPreset(
  data: CreatePresetPayload,
  token: string
): Promise<PromptConfigPreset> {
  const res = await fetch(`${APIURL}/PromptConfigPresets`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Updates an existing preset by Id.
 * The name change (rename) is supported — the backend enforces uniqueness against other rows.
 * Throws if the new name collides with a different existing preset.
 */
export async function UpdatePromptConfigPreset(
  id: string,
  data: UpdatePresetPayload,
  token: string
): Promise<PromptConfigPreset> {
  const res = await fetch(`${APIURL}/PromptConfigPresets/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/** Hard-deletes a preset by Id. The row is permanently removed. */
export async function DeletePromptConfigPreset(id: string, token: string): Promise<void> {
  const res = await fetch(`${APIURL}/PromptConfigPresets/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}
